import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Eye } from "lucide-react";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Reactable } from "../../components/ui/reactable";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISALS } from "../../constants/api";

import StatusPill, { STATUS_META } from "../../components/appraisal/StatusPill";
import useAppraisalRole from "../../hooks/useAppraisalRole";

const STATUS_OPTIONS = [
    "pending_hr_approval",
    "sent_back_by_hr",
    "hr_approved",
    "published",
    "overdue",
    "closed",
].map((s) => ({ value: s, label: STATUS_META[s]?.label || s.replace(/_/g, " ") }));

/* Filters are column-level, so they sit in Reactable's funnel and the screen
   carries none of its own. Export comes from the table too, which is why the
   separate Excel/CSV/Print buttons are gone. */
const buildColumns = (onOpen) => [
    {
        accessor: "employee_name", Header: "Employee", sortable: true, filterable: true,
        Cell: ({ row }) => (
            <div>
                <button
                    className="font-medium hover:text-sidebar-primary text-left"
                    onClick={(e) => { e.stopPropagation(); onOpen(row.id); }}
                >
                    {row.employee_name || "—"}
                </button>
                <p className="text-xs text-muted-foreground">{row.employee_designation}</p>
            </div>
        ),
    },
    {
        accessor: "reporting_manager_name", Header: "RM", sortable: true, filterable: true,
        Cell: ({ value }) => value || "—",
    },
    {
        accessor: "cycle_name", Header: "Cycle", sortable: true, filterable: true,
        Cell: ({ value }) => value || "—",
    },
    {
        accessor: "rm_reviewed_at", Header: "RM Reviewed", sortable: true,
        filterable: true, filterType: "date",
        Cell: ({ value }) => (
            <span className="text-xs text-muted-foreground">
                {value ? dayjs(value).format("DD-MM-YYYY") : "—"}
            </span>
        ),
    },
    {
        accessor: "status", Header: "Status", sortable: true, filterable: true,
        filterType: "select", filterOptions: STATUS_OPTIONS,
        Cell: ({ value }) => <StatusPill value={value} />,
    },
    {
        accessor: "action", Header: "Action",
        Cell: ({ row }) => (
            <Button variant="ghost" size="sm"
                onClick={(e) => { e.stopPropagation(); onOpen(row.id); }}>
                <Eye className="w-4 h-4 mr-1" /> Open
            </Button>
        ),
    },
];

const FinalApprovalList = () => {
    const navigate = useNavigate();
    const { fetch } = useApiMethods();
    const { isHR } = useAppraisalRole();

    const [appraisals, setAppraisals] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadData = useCallback(async () => {
        if (!isHR) { setLoading(false); return; }
        setLoading(true);
        try {
            const res = await fetch(APPRAISALS, { scope: "all" });
            setAppraisals(res?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load appraisals");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHR]);

    useEffect(() => { loadData(); }, [loadData]);

    const rows = useMemo(
        () => appraisals.map((a) => ({
            ...a,
            employee_name: a.employee?.name || "",
            employee_designation: a.employee?.designation || "",
            reporting_manager_name: a.reporting_manager?.name || "",
        })),
        [appraisals],
    );

    if (!isHR) {
        return (
            <ContainerCard scrollable>
                <ModuleTitleCard Icon={CheckCircle2} title="Final Approval" subtitle="HR-only" />
                <div className="text-center py-12 text-muted-foreground">
                    You need HR permission to access this screen.
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={CheckCircle2}
                title="Final Approval"
                subtitle="HR verification, override, and publishing"
                totalRecords={rows.length}
            />

            {loading ? (
                <div className="text-center py-10 text-muted-foreground">Loading…</div>
            ) : (
                <Reactable
                    pagename="final-approval"
                    data={rows}
                    setData={setAppraisals}
                    columns={buildColumns((id) => navigate(`/appraisal/final-approval/${id}`))}
                    isCSVRequired
                    isPDFRequired
                    url={APPRAISALS}
                    printFilter={{ scope: "all" }}
                    rowSize="sm"
                    tableHeight="calc(100vh - 300px)"
                />
            )}
        </ContainerCard>
    );
};

export default FinalApprovalList;
